// @ts-nocheck
/**
 * Swiper + Loox for Rebuy recommended widget (ivvi-rebuy-widget-recommended).
 * Waits for window.Swiper (loaded later in body than section scripts).
 * Loox: try common globals/events after DOM updates (see refreshLooxIn).
 */

/** Swiper breakpoints use min-width (px). */
const BP_2 = 750;
const BP_3 = 990;
const BP_4 = 1200;
const INIT_POLL_MS = 50;
const INIT_POLL_MAX_MS = 12000;
const DEBOUNCE_MS = 250;
const NON_MOBILE_MIN_WIDTH = BP_2;

/**
 * Rebuy Smart Cart integration.
 * Opens Smart Cart after add-to-cart from a specific Rebuy widget.
 *
 * Widget: Recommended (Cart drawer) id284056
 */
const IVVI_REBUY_RECOMMENDED_WIDGET_ID = '284056';
const SMARTCART_READY_TIMEOUT_MS = 12000;

/**
 * @returns {Promise<boolean>}
 */
function waitForSmartCartReady() {
  return new Promise((resolve) => {
    const start = Date.now();

    const isReady = () =>
      typeof window.Rebuy === 'object' &&
      window.Rebuy &&
      window.Rebuy.SmartCart &&
      window.Rebuy.SmartCart.status === 'ready';

    if (isReady()) {
      resolve(true);
      return;
    }

    /** @type {number | null} */
    let timer = null;

    const cleanup = () => {
      if (timer != null) window.clearInterval(timer);
      document.removeEventListener('rebuy:smartcart.ready', onReady);
    };

    const onReady = () => {
      cleanup();
      resolve(true);
    };

    document.addEventListener('rebuy:smartcart.ready', onReady, { once: true });

    timer = window.setInterval(() => {
      if (isReady()) {
        cleanup();
        resolve(true);
        return;
      }
      if (Date.now() - start > SMARTCART_READY_TIMEOUT_MS) {
        cleanup();
        resolve(false);
      }
    }, 50);
  });
}

async function showSmartCart() {
  const ready = await waitForSmartCartReady();
  if (!ready) return;

  try {
    window.Rebuy.SmartCart.show();
  } catch {
    /* ignore */
  }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function asIdString(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
}

/**
 * @param {() => void} callback
 */
function whenSwiperReady(callback) {
  if (typeof window.Swiper === 'function') {
    callback();
    return;
  }
  const start = Date.now();
  const id = window.setInterval(() => {
    if (typeof window.Swiper === 'function') {
      window.clearInterval(id);
      callback();
      return;
    }
    if (Date.now() - start > INIT_POLL_MAX_MS) {
      window.clearInterval(id);
    }
  }, INIT_POLL_MS);
}

/**
 * Re-scan Loox star widgets after dynamic HTML (Rebuy Vue).
 * Tries window.loox / LOOX / Loox init-style APIs, then a custom event.
 */
function refreshLooxIn(container) {
  if (!container) return;

  const tryGlobals = () => {
    const candidates = [window.loox, window.LOOX, window.Loox];
    for (const api of candidates) {
      if (!api || typeof api !== 'object') continue;
      for (const method of ['init', 'reInit', 'reload', 'refresh']) {
        if (typeof api[method] === 'function') {
          try {
            api[method]();
            return true;
          } catch {
            /* ignore */
          }
        }
      }
    }
    return false;
  };

  if (tryGlobals()) return;

  try {
    window.dispatchEvent(new CustomEvent('loox:refresh'));
  } catch {
    /* ignore */
  }
}

function currentSlidesPerView() {
  // Mirrors Swiper config in this file (1/2/3/4 per view).
  if (window.matchMedia(`(min-width: ${BP_4}px)`).matches) return 4;
  if (window.matchMedia(`(min-width: ${BP_3}px)`).matches) return 3;
  if (window.matchMedia(`(min-width: ${BP_2}px)`).matches) return 2;
  return 1;
}

/**
 * Show arrows only when there is overflow on non-mobile widths.
 *
 * Rule:
 * - At >= 750px, hide arrows when slideCount <= slidesPerView for current breakpoint.
 * - Otherwise show them.
 */
function updateArrowVisibility(controls, prevEl, nextEl, slideCount) {
  if (!controls) return;

  const isNonMobile = window.matchMedia(`(min-width: ${NON_MOBILE_MIN_WIDTH}px)`).matches;
  const spv = currentSlidesPerView();
  const shouldHideArrows = isNonMobile && slideCount <= spv;

  // Debuggable state in DOM (safe in production).
  try {
    controls.dataset.ivviRebuySlideCount = String(slideCount);
    controls.dataset.ivviRebuySlidesPerView = String(spv);
    controls.dataset.ivviRebuyArrowsHidden = shouldHideArrows ? '1' : '0';
  } catch {
    /* ignore */
  }

  if (prevEl instanceof HTMLElement) {
    prevEl.hidden = shouldHideArrows;
    if (shouldHideArrows) {
      prevEl.style.display = 'none';
      prevEl.setAttribute('aria-hidden', 'true');
      prevEl.setAttribute('tabindex', '-1');
    } else {
      prevEl.style.removeProperty('display');
      prevEl.removeAttribute('aria-hidden');
      prevEl.removeAttribute('tabindex');
    }
  }

  if (nextEl instanceof HTMLElement) {
    nextEl.hidden = shouldHideArrows;
    if (shouldHideArrows) {
      nextEl.style.display = 'none';
      nextEl.setAttribute('aria-hidden', 'true');
      nextEl.setAttribute('tabindex', '-1');
    } else {
      nextEl.style.removeProperty('display');
      nextEl.removeAttribute('aria-hidden');
      nextEl.removeAttribute('tabindex');
    }
  }
}

/**
 * @param {HTMLElement} sectionRoot
 */
function initRebuySwiper(sectionRoot) {
  const host = sectionRoot.querySelector('[data-rebuy-id]');
  if (!host) return null;

  const swiperEl = host.querySelector('[data-ivvi-rebuy-swiper]');
  if (!swiperEl) return null;

  const wrapper = swiperEl.querySelector('.swiper-wrapper');
  if (!wrapper) return null;

  const slides = wrapper.querySelectorAll('.swiper-slide');
  const slideCount = slides.length;
  if (slideCount === 0) return null;

  const prevCount = sectionRoot.dataset.ivviRebuySlideCount ? Number(sectionRoot.dataset.ivviRebuySlideCount) : -1;

  const existing = swiperEl.ivviRebuySwiper;
  if (existing && !existing.destroyed && prevCount === slideCount) {
    existing.update();
    window.requestAnimationFrame(() => {
      refreshLooxIn(host);
      window.requestAnimationFrame(() => refreshLooxIn(host));
    });
    return existing;
  }

  if (existing && !existing.destroyed) {
    try {
      existing.destroy(true, true);
    } catch {
      /* ignore */
    }
  }
  swiperEl.ivviRebuySwiper = null;

  const controls = sectionRoot.querySelector('[data-ivvi-rebuy-controls]');
  const prevEl = controls?.querySelector('[data-ivvi-rebuy-prev]') ?? undefined;
  const nextEl = controls?.querySelector('[data-ivvi-rebuy-next]') ?? undefined;
  const paginationEl = controls?.querySelector('[data-ivvi-rebuy-pagination]') ?? undefined;

  updateArrowVisibility(controls, prevEl, nextEl, slideCount);

  const swiper = new window.Swiper(swiperEl, {
    // Keep pagination/arrows visible on desktop even when slide count <= slidesPerView.
    watchOverflow: false,
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
    slidesPerView: 1,
    spaceBetween: 16,
    speed: 400,
    grabCursor: true,
    pagination: {
      el: paginationEl,
      clickable: true,
      enabled: true,
    },
    navigation: {
      prevEl,
      nextEl,
      enabled: true,
    },
    breakpoints: {
      [BP_2]: {
        slidesPerView: 2,
        spaceBetween: 16,
      },
      [BP_3]: {
        slidesPerView: 3,
        spaceBetween: 18,
      },
      [BP_4]: {
        slidesPerView: 4,
        spaceBetween: 20,
      },
    },
    on: {
      afterInit(s) {
        s.update();
        updateArrowVisibility(controls, prevEl, nextEl, slideCount);
        window.setTimeout(() => {
          s.updateSize();
          s.update();
          updateArrowVisibility(controls, prevEl, nextEl, slideCount);
        }, 100);
        window.setTimeout(() => {
          s.updateSize();
          s.update();
          updateArrowVisibility(controls, prevEl, nextEl, slideCount);
        }, 400);
      },
    },
  });

  swiperEl.ivviRebuySwiper = swiper;
  sectionRoot.dataset.ivviRebuySlideCount = String(slideCount);

  window.requestAnimationFrame(() => {
    refreshLooxIn(host);
    window.requestAnimationFrame(() => refreshLooxIn(host));
  });

  return swiper;
}

/**
 * @param {HTMLElement} sectionRoot
 */
function setupSection(sectionRoot) {
  const host = sectionRoot.querySelector('[data-rebuy-id]');
  if (!host) return;

  let debounceTimer = 0;

  const scheduleInit = () => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      whenSwiperReady(() => {
        initRebuySwiper(sectionRoot);
      });
    }, DEBOUNCE_MS);
  };

  const observer = new MutationObserver(() => {
    scheduleInit();
  });

  observer.observe(host, { childList: true, subtree: true });
  scheduleInit();

  window.addEventListener(
    'resize',
    () => {
      const swiperEl = host.querySelector('[data-ivvi-rebuy-swiper]');
      const inst = swiperEl?.ivviRebuySwiper;
      if (inst && !inst.destroyed) {
        const wrapper = swiperEl?.querySelector('.swiper-wrapper');
        const slideCount = wrapper ? wrapper.querySelectorAll('.swiper-slide').length : 0;
        const controls = sectionRoot.querySelector('[data-ivvi-rebuy-controls]');
        const prevEl = controls?.querySelector('[data-ivvi-rebuy-prev]') ?? undefined;
        const nextEl = controls?.querySelector('[data-ivvi-rebuy-next]') ?? undefined;

        updateArrowVisibility(controls, prevEl, nextEl, slideCount);
        inst.update();
      }
    },
    { passive: true }
  );
}

function boot() {
  document.querySelectorAll('[data-ivvi-rebuy-section]').forEach((section) => {
    if (!(section instanceof HTMLElement)) return;
    if (section.dataset.ivviRebuyBoot === '1') return;
    section.dataset.ivviRebuyBoot = '1';
    setupSection(section);
  });

  // Open Rebuy Smart Cart after add-to-cart from the recommended widget.
  if (document.documentElement.dataset.ivviRebuySmartCartHooked === '1') return;
  document.documentElement.dataset.ivviRebuySmartCartHooked = '1';

  document.addEventListener('rebuy.add', (event) => {
    /** @type {{ detail?: { widget?: { id?: string|number }, product?: unknown }}} */
    // @ts-ignore - runtime event payload from Rebuy
    const e = event;
    const widgetId = asIdString(e?.detail?.widget?.id);
    if (widgetId !== IVVI_REBUY_RECOMMENDED_WIDGET_ID) return;
    showSmartCart();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', boot);
